/**
 * Token Graph em sessionStorage criptografado (AES-GCM + PBKDF2).
 * Contexto deve incluir chaveSistema + usuário + estabelecimento para invalidar entre sessões.
 */

const STORAGE_KEY = "cf_whatsapp_official_v1"

const SALT = "cf-whatsapp-official-v1"

function u8ToB64(u8) {
    let s = ""
    for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i])
    return btoa(s)
}

function b64ToU8(b64) {
    const bin = atob(b64)
    const u8 = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i)
    return u8
}

async function deriveKey(passphrase) {
    const enc = new TextEncoder()
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        enc.encode(passphrase),
        "PBKDF2",
        false,
        ["deriveBits", "deriveKey"]
    )
    return crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: enc.encode(SALT),
            iterations: 120000,
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"]
    )
}

/**
 * @param {{ token: string, wabaid: string, contextKey: string, conta?: string, telefone?: string }} opts
 * conta = WhatsApp Business Account ID (POST …/message_templates). telefone = linha cadastrada na API.
 */
export async function saveWhatsappOfficialSession({
    token,
    wabaid,
    contextKey,
    conta = "",
    telefone = "",
}) {
    if (!crypto?.subtle) {
        console.warn("Web Crypto indisponível; token não persistido em sessionStorage.")
        return
    }
    const key = await deriveKey(contextKey)
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const plaintext = new TextEncoder().encode(token)
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext)
    const combined = new Uint8Array(iv.length + ciphertext.byteLength)
    combined.set(iv)
    combined.set(new Uint8Array(ciphertext), iv.length)
    const payload = {
        v: 1,
        wabaid: String(wabaid),
        conta: conta != null && String(conta).trim() !== "" ? String(conta).trim() : "",
        telefone: telefone != null && String(telefone).trim() !== "" ? String(telefone).trim() : "",
        data: u8ToB64(combined),
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

/**
 * @param {string} contextKey
 * @returns {Promise<{ token: string, wabaid: string, conta: string, telefone: string } | null>}
 */
export async function loadWhatsappOfficialSession(contextKey) {
    if (!crypto?.subtle) return null
    let payload
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        payload = JSON.parse(raw)
    } catch {
        return null
    }
    if (!payload?.data || !payload?.wabaid) return null
    let combined
    try {
        combined = b64ToU8(payload.data)
    } catch {
        return null
    }
    const iv = combined.slice(0, 12)
    const ciphertext = combined.slice(12)
    try {
        const key = await deriveKey(contextKey)
        const decrypted = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            key,
            ciphertext
        )
        const token = new TextDecoder().decode(decrypted)
        if (!token) return null
        return {
            token,
            wabaid: String(payload.wabaid),
            conta: payload.conta != null ? String(payload.conta) : "",
            telefone: payload.telefone != null ? String(payload.telefone) : "",
        }
    } catch {
        return null
    }
}

export function clearWhatsappOfficialSession() {
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        /* ignore */
    }
}

export function buildWhatsappSessionContextKey(chaveSistema, userId, idEstabelecimento) {
    return `${chaveSistema || ""}|${userId || ""}|${idEstabelecimento || ""}`
}
